"use client"

import { JobCardPublic } from "./job-card-public"
import { JobCardSkeleton } from "./job-card-skeleton"
import { Typography } from "./typography"
import { Button } from "./button"
import { cn } from "../../lib/utils"
import { ProfileAvatar } from "./profile-avatar"

export interface JobNotFoundJob {
  id: string
  title: string
  company: string
  logo?: string | null
  location?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  salary: string
  experience: string
  workMode: string
  skills?: string[]
  hideSalary?: boolean
  href?: string
}

export interface JobNotFoundContentProps {
  /** Creator profile path for the back button, e.g. "/@username" */
  profilePath: string
  /** Creator display name shown next to avatar in the header */
  creatorName?: string | null
  /** Creator avatar URL shown in the header */
  creatorAvatarUrl?: string | null
  /** Jobs to display (similar or creator jobs) */
  jobs?: JobNotFoundJob[]
  /** Loading state — shows skeleton cards */
  isLoading?: boolean
  /** Callback when a job card is clicked (only called if href is not provided) */
  onJobClick?: (job: JobNotFoundJob) => void
  /** Section heading label (localized by consumer) */
  sectionLabel?: string
  /** Illustration image src for the not-found state */
  illustrationSrc?: string

  // ---- Localizable text (consumer passes translated strings) ----
  title?: string
  subtitle?: string
  noJobsText?: string
  viewProfileText?: string

  className?: string
}

export function JobNotFoundContent({
  profilePath,
  creatorName,
  creatorAvatarUrl,
  jobs = [],
  isLoading = false,
  onJobClick,
  sectionLabel = "Similar Jobs",
  illustrationSrc,
  title = "Job you are looking for is not available",
  subtitle = "Looks like this role has been filled or is no longer active. Explore similar opportunities.",
  noJobsText = "No matching jobs found at this time.",
  viewProfileText = "View Profile",
  className,
}: JobNotFoundContentProps) {
  const displayName = creatorName || "Creator"

  return (
    <div
      id="creator-job-not-found-page"
      data-experiment-id="creator-job-not-found-page"
      className={cn("creator-job-not-found-page flex flex-col min-h-screen bg-[#FFF8E8]", className)}
    >

      {/* Sticky header — creator avatar + name */}
      <div className="sticky top-0 z-30 bg-[#FFF8E8] px-4 py-3">
        <div className="flex items-center gap-1 min-w-0">
          <a
            href={profilePath}
            aria-label="Back to profile"
            className="rounded-md p-1 text-[#344054] shrink-0"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12.5 4.16667L7.5 10L12.5 15.8333"
                stroke="#344054"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
          <div className="flex items-center gap-2 min-w-0">
            <ProfileAvatar
              src={creatorAvatarUrl}
              alt={displayName}
              className="h-8 w-8 shrink-0 rounded-full border border-black"
              fallbackClassName="bg-white"
              logoClassName="h-[52%] w-[52%]"
            />
            <Typography
              variant="small"
              className="text-[#344054] text-[14px] font-medium truncate"
            >
              {displayName}
            </Typography>
          </div>
        </div>
      </div>

      {/* Illustration + not-found message */}
      <div className="flex flex-col items-center gap-3 px-4 pt-4 pb-6 text-center">
        {illustrationSrc && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={illustrationSrc}
            alt="job not found illustration"
            className="h-[100px] w-[100px] object-contain select-none"
            draggable={false}
          />
        )}
        <div className="flex flex-col gap-2">
          <Typography
            variant="h6"
            as="h1"
            className="text-[#101828] text-[18px] font-bold leading-tight"
          >
            {title}
          </Typography>
          <Typography
            variant="muted"
            className="text-[#667085] text-[14px] leading-relaxed"
          >
            {subtitle}
          </Typography>
        </div>
      </div>

      {/* Job cards */}
      <div
        id="creator-job-not-found-jobs-section"
        data-experiment-id="creator-jobs-section"
        className="creator-jobs-section flex flex-col gap-3 px-4 pb-20"
      >
        {isLoading ? (
          <>
            <Typography variant="small" className="text-[#344054] font-medium">
              {sectionLabel}
            </Typography>
            {Array.from({ length: 4 }).map((_, i) => (
              <JobCardSkeleton key={i} />
            ))}
          </>
        ) : jobs.length > 0 ? (
          <>
            <Typography variant="small" className="text-[#344054] font-medium">
              {sectionLabel}
            </Typography>
            {jobs.map((job, index) => {
              const card = (
                <JobCardPublic
                  jobTitle={job.title}
                  companyName={job.company}
                  companyLogo={job.logo || ""}
                  location={job.location || ""}
                  city={job.city}
                  state={job.state}
                  country={job.country}
                  salary={job.salary}
                  experience={job.experience}
                  workMode={job.workMode}
                  skills={job.skills}
                  hideSalary={job.hideSalary}
                />
              )

              if (job.href) {
                return (
                  <a
                    key={job.id}
                    id={`creator-job-not-found-job-card-${index}`}
                    data-experiment-id="creator-job-card"
                    href={job.href}
                    className="creator-job-card-link block w-full text-left"
                  >
                    {card}
                  </a>
                )
              }

              return (
                <div
                  key={job.id}
                  id={`creator-job-not-found-job-card-action-${index}`}
                  data-experiment-id="creator-job-card"
                  className="creator-job-card-action relative block w-full"
                >
                  <button
                    type="button"
                    id={`creator-job-not-found-job-card-button-${index}`}
                    data-experiment-id="creator-job-card-button"
                    className="absolute inset-0 z-20 rounded-[8px] border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D41E1] focus-visible:ring-offset-2"
                    aria-label={`View ${job.title} job`}
                    onClick={() => onJobClick?.(job)}
                  />
                  <div className="pointer-events-none">{card}</div>
                </div>
              )
            })}
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <Typography variant="muted" className="text-[#667085] text-[14px]">
              {noJobsText}
            </Typography>
            <Button
              asChild
              className="h-10 px-6 bg-[#0D41E1] text-white border-black"
            >
              <a href={profilePath}>{viewProfileText}</a>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
